import { useEffect, useMemo, useState } from "react";
import { DeliveryChip } from "../../components/DeliveryChip";
import { collection, collectionGroup, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { FileUp, Plus, Trash2 } from "lucide-react";
import { PO_STATUS_LABEL, paths, poSubtotalCents, type OrderedItem, type PricebookItem, type PurchaseOrder, type PurchaseOrderStatus, type Vendor } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call, CallError } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { textareaClass } from "../../components/JobEditCard";
import { SearchBox, useListView } from "../../components/ListControls";

// Purchase orders to suppliers. Start one from the parts jobs are waiting on
// or from low stock, email it as a PDF, read the supplier's confirmation with
// AI, and receive it into stock (the jobs' parts arrive with it).

type PoRow = PurchaseOrder & { id: string };
type ReadyPart = OrderedItem & { id: string };
type Item = PricebookItem & { id: string };
type VendorRow = Vendor & { id: string };

interface DraftLine {
  key?: string;
  description: string;
  sku: string;
  itemId: string | null;
  quantity: number;
  unitCostCents: number | null;
  jobId: string | null;
  jobTitle?: string;
  orderedItemId: string | null;
}

const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const toDateInput = (ms: number | null) => (ms ? new Date(ms).toISOString().slice(0, 10) : "");
const fromDateInput = (s: string) => (s ? Date.parse(`${s}T12:00:00Z`) : null);
const STATUS_STYLE: Record<PurchaseOrderStatus, string> = {
  draft: "bg-steel/15 text-ink",
  sent: "bg-brandorange/15 text-brandorange-dark",
  acknowledged: "bg-ink/10 text-ink",
  partial: "bg-brandorange/25 text-brandorange-dark",
  received: "bg-success/15 text-success",
  canceled: "bg-steel/10 text-steel line-through",
};
const errText = (err: unknown) => {
  const e = err as CallError;
  return e.hint ? `${e.message} ${e.hint}` : e.message;
};

export function PurchasingPage() {
  const { tenantId } = useAuth();
  const [pos, setPos] = useState<PoRow[]>([]);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [ready, setReady] = useState<ReadyPart[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ poId?: string; init: Omit<Partial<PurchaseOrder>, "lines"> & { lines: DraftLine[] } } | null>(null);
  const [status, setStatus] = useState<"open" | "all" | PurchaseOrderStatus>("open");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantPurchaseOrders(tenantId)), orderBy("createdAt", "desc")), (s) => setPos(s.docs.map((d) => ({ id: d.id, ...(d.data() as PurchaseOrder) })))),
      onSnapshot(collection(db, paths.tenantVendors(tenantId)), (s) => setVendors(s.docs.map((d) => ({ id: d.id, ...(d.data() as Vendor) })).filter((v) => !v.archivedAt).sort((a, b) => a.name.localeCompare(b.name)))),
      onSnapshot(collection(db, paths.tenantPricebook(tenantId)), (s) => setItems(s.docs.map((d) => ({ id: d.id, ...(d.data() as PricebookItem) })).filter((p) => !p.archivedAt && p.kind !== "kit"))),
      onSnapshot(query(collectionGroup(db, "orderedItems"), where("tenantId", "==", tenantId), where("status", "==", "ready")), (s) => setReady(s.docs.map((d) => ({ id: d.id, ...(d.data() as OrderedItem) })))),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  // What's waiting to be ordered: the jobs' ready parts by supplier, and low stock.
  const readyByVendor = useMemo(() => {
    const m = new Map<string, ReadyPart[]>();
    for (const p of ready) {
      if (pos.some((po) => po.status === "draft" && po.lines.some((l) => l.orderedItemId === p.id))) continue; // already on a draft
      const k = p.vendor || "";
      m.set(k, [...(m.get(k) ?? []), p]);
    }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [ready, pos]);
  const lowStock = useMemo(() => items.filter((p) => p.trackInventory && p.reorderLevel !== null && p.onHandQty <= p.reorderLevel), [items]);

  const shown = pos.filter((p) => (status === "all" ? true : status === "open" ? ["draft", "sent", "acknowledged", "partial"].includes(p.status) : p.status === status));
  const view = useListView(shown, {
    text: (p) => [String(p.number), p.vendorName, PO_STATUS_LABEL[p.status], ...p.lines.map((l) => l.description), ...p.lines.map((l) => l.jobTitle)],
    sorts: { number: (a, b) => b.number - a.number },
    defaultSort: "number",
    pageSize: 50,
  });

  function startFromParts(vendorName: string, parts: ReadyPart[]) {
    const v = vendors.find((x) => x.name.toLowerCase() === vendorName.toLowerCase());
    setDraft({
      init: {
        vendorId: v?.id ?? null,
        vendorName: v?.name ?? vendorName,
        lines: parts.map((p) => {
          const it = p.itemId ? items.find((i) => i.id === p.itemId) : undefined;
          return { description: p.description, sku: it?.sku ?? "", itemId: p.itemId, quantity: p.quantity, unitCostCents: it?.costCents ?? null, jobId: p.jobId, orderedItemId: p.id };
        }),
      },
    });
  }
  function startFromLowStock() {
    setDraft({
      init: {
        lines: lowStock.map((p) => ({ description: p.name, sku: p.sku, itemId: p.id, quantity: Math.max(1, (p.reorderLevel ?? 0) * 2 - p.onHandQty), unitCostCents: p.costCents, jobId: null, orderedItemId: null })),
      },
    });
  }

  if (!tenantId) return null;
  if (draft) {
    return <PoEditor tenantId={tenantId} poId={draft.poId} init={draft.init} vendors={vendors} items={items} onDone={(msg) => { setDraft(null); if (msg) setNote(msg); }} />;
  }
  const openPo = open ? pos.find((p) => p.id === open) : null;
  if (openPo) {
    return <PoDetail tenantId={tenantId} po={openPo} onBack={() => setOpen(null)} onEdit={() => setDraft({ poId: openPo.id, init: { ...openPo } })} />;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Purchase orders</h1>
        <div className="flex items-center gap-2">
          <AckUpload tenantId={tenantId} onRead={(id) => setOpen(id)} say={setNote} />
          <Button onClick={() => setDraft({ init: { lines: [{ description: "", sku: "", itemId: null, quantity: 1, unitCostCents: null, jobId: null, orderedItemId: null }] } })}>
            <Plus size={14} className="mr-1 inline" /> New PO
          </Button>
        </div>
      </div>
      {note ? <p className="rounded bg-success/10 px-2 py-1 text-xs text-ink">{note}</p> : null}

      {readyByVendor.length || lowStock.length ? (
        <Card className="space-y-1.5">
          <CardTitle className="mb-0">To order</CardTitle>
          {readyByVendor.map(([vendor, parts]) => (
            <div key={vendor || "(none)"} className="flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate text-ink">
                {parts.length} part{parts.length === 1 ? "" : "s"} jobs are waiting on{vendor ? ` from ${vendor}` : " (no supplier yet)"}
                <span className="text-xs text-steel"> · {parts.slice(0, 3).map((p) => p.description).join(", ")}{parts.length > 3 ? "…" : ""}</span>
              </span>
              <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={() => startFromParts(vendor, parts)}>
                Start a PO
              </Button>
            </div>
          ))}
          {lowStock.length ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate text-ink">
                {lowStock.length} item{lowStock.length === 1 ? " is" : "s are"} at or below the reorder level
                <span className="text-xs text-steel"> · {lowStock.slice(0, 3).map((p) => p.name).join(", ")}{lowStock.length > 3 ? "…" : ""}</span>
              </span>
              <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={startFromLowStock}>
                Start a PO
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={view.query} onChange={view.setQuery} placeholder="Search number, supplier, part, job…" />
        <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="open">Open</option>
          <option value="all">All</option>
          {(Object.keys(PO_STATUS_LABEL) as PurchaseOrderStatus[]).map((s) => (
            <option key={s} value={s}>
              {PO_STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
      </div>
      <Card className="p-0">
        {view.page.map((p) => (
          <button key={p.id} className="flex h-10 w-full items-center gap-3 border-b border-steel/10 px-4 text-left text-sm last:border-0 hover:bg-cream/50" onClick={() => setOpen(p.id)}>
            <span className="w-14 shrink-0 font-semibold text-ink">#{p.number}</span>
            <span className="min-w-0 flex-1 truncate text-ink">
              {p.vendorName}
              <span className="text-xs text-steel">
                {" "}
                · {p.lines.length} line{p.lines.length === 1 ? "" : "s"}
                {p.lines.some((l) => l.jobId) ? ` · for ${[...new Set(p.lines.filter((l) => l.jobTitle).map((l) => l.jobTitle))].slice(0, 2).join(", ")}` : ""}
              </span>
            </span>
            {p.expectedAt && p.status !== "received" ? <span className="hidden text-xs text-steel sm:inline">due {fmtDay(p.expectedAt)}</span> : null}
            <span className="w-24 text-right text-xs text-ink">{money(p.subtotalCents)}</span>
            <span className={cn("w-28 shrink-0 rounded-full px-2 py-0.5 text-center text-xs", STATUS_STYLE[p.status])}>{PO_STATUS_LABEL[p.status]}</span>
          </button>
        ))}
        {view.filtered.length === 0 ? <p className="px-4 py-8 text-center text-sm text-steel">{pos.length ? "Nothing matches." : "No purchase orders yet. Start one from the parts jobs are waiting on, or New PO."}</p> : null}
      </Card>
      {view.filtered.length > view.page.length ? (
        <button className="w-full text-center text-xs text-steel hover:text-ink" onClick={view.all}>
          Show all {view.filtered.length}
        </button>
      ) : null}
    </div>
  );
}

// ---- Editor (new, or a draft) ----------------------------------------------

function PoEditor({ tenantId, poId, init, vendors, items, onDone }: { tenantId: string; poId?: string; init: Omit<Partial<PurchaseOrder>, "lines"> & { lines: DraftLine[] }; vendors: VendorRow[]; items: Item[]; onDone: (msg?: string) => void }) {
  const [vendorId, setVendorId] = useState(init.vendorId ?? "");
  const [vendorName, setVendorName] = useState(init.vendorName ?? "");
  const [vendorEmail, setVendorEmail] = useState(init.vendorEmail ?? "");
  const [lines, setLines] = useState<DraftLine[]>(init.lines);
  const [shipTo, setShipTo] = useState(init.shipTo ?? "");
  const [expected, setExpected] = useState(toDateInput(init.expectedAt ?? null));
  const [notes, setNotes] = useState(init.notes ?? "");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const vendor = vendors.find((v) => v.id === vendorId);
  const matches = pick.trim() ? items.filter((i) => `${i.name} ${i.sku} ${i.category}`.toLowerCase().includes(pick.trim().toLowerCase())).slice(0, 8) : [];
  const setLine = (i: number, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l, n) => (n === i ? { ...l, ...patch } : l)));

  async function save(then: "close" | "place") {
    const kept = lines.filter((l) => l.description.trim());
    if (!kept.length) return setError("Add at least one line.");
    if (!vendorId && !vendorName.trim()) return setError("Pick the supplier.");
    setBusy(true);
    setError("");
    try {
      const res = await call<{ poId: string; number: number }>("savePurchaseOrder", {
        tenantId,
        ...(poId ? { poId } : {}),
        vendorId: vendorId || null,
        vendorName: vendorId ? "" : vendorName,
        vendorEmail,
        lines: kept.map((l) => ({ key: l.key, description: l.description, sku: l.sku, itemId: l.itemId, quantity: Number(l.quantity) || 1, unitCostCents: l.unitCostCents, jobId: l.jobId, orderedItemId: l.orderedItemId })),
        shipTo,
        notes,
        expectedAt: fromDateInput(expected),
      });
      if (then === "place") {
        await call("setPurchaseOrderStatus", { tenantId, poId: res.poId, action: "send" });
        onDone(`PO #${res.number} placed. Email it from the PO, or it's on order as is.`);
      } else onDone(`PO #${res.number} saved as a draft.`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Card className="space-y-3">
        <CardTitle className="mb-0">{poId ? `Edit PO #${init.number}` : "New purchase order"}</CardTitle>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Supplier</Label>
            <Select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">{vendors.length ? "Not on file (type it below)" : "No suppliers on file yet"}</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
            {!vendorId ? <Input className="mt-1" value={vendorName} placeholder="Supplier name" onChange={(e) => setVendorName(e.target.value)} /> : null}
          </div>
          <div>
            <Label>Supplier email (for sending it)</Label>
            <Input value={vendorEmail} placeholder={vendor?.email || "orders@supplier.com"} onChange={(e) => setVendorEmail(e.target.value)} />
          </div>
        </div>

        <div>
          <div className="grid grid-cols-[1fr_7rem_4.5rem_6rem_1.5rem] gap-2 text-xs text-steel">
            <span>Item</span>
            <span>Part #</span>
            <span>Qty</span>
            <span>Unit cost</span>
            <span />
          </div>
          {lines.map((l, i) => (
            <div key={i} className="mt-1 grid grid-cols-[1fr_7rem_4.5rem_6rem_1.5rem] items-start gap-2">
              <div>
                <Input value={l.description} placeholder="What you're ordering" onChange={(e) => setLine(i, { description: e.target.value })} />
                {l.jobId ? <p className="mt-0.5 text-[11px] text-steel">For a job's part{l.jobTitle ? `: ${l.jobTitle}` : ""}</p> : null}
              </div>
              <Input value={l.sku} onChange={(e) => setLine(i, { sku: e.target.value })} />
              <Input type="number" min="0.01" step="1" value={l.quantity} onChange={(e) => setLine(i, { quantity: parseFloat(e.target.value) || 0 })} />
              <Input type="number" min="0" step="0.01" value={l.unitCostCents !== null ? (l.unitCostCents / 100).toString() : ""} placeholder="—" onChange={(e) => setLine(i, { unitCostCents: e.target.value === "" ? null : Math.round(parseFloat(e.target.value) * 100) })} />
              <button className="mt-2 text-steel hover:text-danger" onClick={() => setLines((ls) => ls.filter((_, n) => n !== i))} aria-label="Remove line">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="mt-2 flex flex-wrap items-start gap-2">
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setLines((ls) => [...ls, { description: "", sku: "", itemId: null, quantity: 1, unitCostCents: null, jobId: null, orderedItemId: null }])}>
              <Plus size={13} className="mr-1 inline" /> Blank line
            </Button>
            <div className="relative w-64">
              <Input className="py-1 text-xs" value={pick} placeholder="Add from pricebook…" onChange={(e) => setPick(e.target.value)} />
              {matches.length ? (
                <div className="absolute z-20 mt-1 w-full rounded-lg border border-steel/30 bg-white p-1 shadow-lg">
                  {matches.map((it) => (
                    <button
                      key={it.id}
                      className="flex w-full justify-between rounded px-2 py-1 text-left text-xs hover:bg-cream"
                      onClick={() => {
                        setLines((ls) => [...ls.filter((x) => x.description.trim()), { description: it.name, sku: it.sku, itemId: it.id, quantity: 1, unitCostCents: it.costCents, jobId: null, orderedItemId: null }]);
                        setPick("");
                      }}
                    >
                      <span className="truncate text-ink">{it.name}</span>
                      <span className="ml-2 shrink-0 text-steel">{it.trackInventory ? `${it.onHandQty} on hand` : it.sku}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <span className="ml-auto text-sm font-semibold text-ink">Total {money(poSubtotalCents(lines.filter((l) => l.description.trim())))}</span>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Ship to</Label>
            <textarea className={textareaClass} rows={2} value={shipTo} placeholder="Your yard, or the job site" onChange={(e) => setShipTo(e.target.value)} />
          </div>
          <div>
            <Label>Needed by</Label>
            <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Notes for the supplier</Label>
          <textarea className={textareaClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button busy={busy} onClick={() => void save("close")}>
            Save draft
          </Button>
          <Button variant="secondary" busy={busy} onClick={() => void save("place")}>
            Save and mark placed
          </Button>
          <Button variant="ghost" onClick={() => onDone()}>
            Cancel
          </Button>
        </div>
        <p className="text-xs text-steel">To email it to the supplier as a PDF, save it, then open it and use Email.</p>
      </Card>
    </div>
  );
}

// ---- One PO: email, confirmation, receiving ----------------------------------

function PoDetail({ tenantId, po, onBack, onEdit }: { tenantId: string; po: PoRow; onBack: () => void; onEdit: () => void }) {
  const [panel, setPanel] = useState<"" | "email" | "receive">("");
  // A confirmation read from the list arrives here to review.
  const [ack, setAck] = useState<AckResult | null>(() => {
    try {
      const raw = sessionStorage.getItem(`clevis.ack.${po.id}`);
      sessionStorage.removeItem(`clevis.ack.${po.id}`);
      return raw ? (JSON.parse(raw) as AckResult) : null;
    } catch {
      return null;
    }
  });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const openPo = ["sent", "acknowledged", "partial"].includes(po.status);

  async function act(fn: string, data: Record<string, unknown>, done: string) {
    setBusy(true);
    setError("");
    try {
      await call(fn, { tenantId, poId: po.id, ...data });
      setMsg(done);
      setPanel("");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }
  async function pdf() {
    try {
      const res = await call<{ base64: string; filename: string }>("getPurchaseOrderPdf", { tenantId, poId: po.id }, { mutation: false });
      const bytes = Uint8Array.from(atob(res.base64), (ch) => ch.charCodeAt(0));
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      a.download = res.filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(errText(err));
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-3">
      <button className="text-xs text-steel hover:text-ink" onClick={onBack}>
        ← Purchase orders
      </button>
      <Card className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-lg font-bold text-ink">
              PO #{po.number} · {po.vendorName}
            </h1>
            <p className="text-xs text-steel">
              {money(po.subtotalCents)} · made by {po.createdByName || "—"} {fmtDay(po.createdAt)}
              {po.sentAt ? ` · placed ${fmtDay(po.sentAt)}` : ""}
              {po.expectedAt ? ` · due ${fmtDay(po.expectedAt)}` : ""}
              {po.receivedAt ? ` · received ${fmtDay(po.receivedAt)}` : ""}
            </p>
          </div>
          <span className={cn("rounded-full px-2.5 py-0.5 text-xs", STATUS_STYLE[po.status])}>{PO_STATUS_LABEL[po.status]}</span>
        </div>

        <div className="divide-y divide-steel/10 rounded-lg border border-steel/15 text-sm">
          {po.lines.map((l) => (
            <div key={l.key} className="flex items-center gap-3 px-3 py-1.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-ink">
                  {l.description}
                  {l.sku ? <span className="text-xs text-steel"> · {l.sku}</span> : null}
                </div>
                {l.jobTitle || l.ackNote ? (
                  <div className="truncate text-[11px] text-steel">
                    {l.jobTitle ? `For ${l.jobTitle}` : ""}
                    {l.jobTitle && l.ackNote ? " · " : ""}
                    {l.ackNote ? <span className="text-brandorange-dark">Supplier: {l.ackNote}</span> : null}
                  </div>
                ) : null}
              </div>
              <span className="w-24 text-right text-xs text-steel">{po.status === "draft" ? `qty ${l.quantity}` : `${l.receivedQty} / ${l.quantity} in`}</span>
              <span className="w-20 text-right text-xs text-ink">{l.unitCostCents !== null ? money(l.unitCostCents) : "—"}</span>
            </div>
          ))}
        </div>

        {po.acknowledgment ? (
          <div className="rounded-lg bg-cream/60 px-3 py-2 text-xs text-ink">
            <strong>Confirmed by the supplier</strong>
            {po.acknowledgment.vendorRef ? ` (their order ${po.acknowledgment.vendorRef})` : ""} · read {fmtDay(po.acknowledgment.at)} by {po.acknowledgment.byName}
            {po.acknowledgment.expectedAt ? ` · ships for ${fmtDay(po.acknowledgment.expectedAt)}` : ""}
            {po.acknowledgment.differences.length ? (
              <ul className="mt-1 list-disc pl-4 text-steel">
                {po.acknowledgment.differences.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {po.emails.length ? (
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-steel">
            Emailed
            {po.emails.filter((e) => e.ok).length
              ? po.emails
                  .filter((e) => e.ok)
                  .map((e, i) => (
                    <span key={i} className="inline-flex items-center gap-1">
                      {fmtDay(e.at)} to {e.to.join(", ")} <DeliveryChip delivery={e.id ? po.emailDelivery?.[e.id] : null} />
                    </span>
                  ))
              : " (the email didn't go out)"}
          </p>
        ) : null}
        {po.shipTo ? <p className="whitespace-pre-line text-xs text-steel">Ship to: {po.shipTo}</p> : null}
        {po.notes ? <p className="whitespace-pre-line text-xs text-steel">Notes: {po.notes}</p> : null}

        {msg ? <p className="rounded bg-success/10 px-2 py-1 text-xs text-ink">{msg}</p> : null}
        {error ? <p className="text-xs text-danger">{error}</p> : null}

        <div className="flex flex-wrap items-center gap-2">
          {po.status === "draft" ? (
            <>
              <Button onClick={onEdit}>Edit</Button>
              <Button variant="secondary" busy={busy} onClick={() => void act("setPurchaseOrderStatus", { action: "send" }, "Placed: the parts are on order.")}>
                Mark placed
              </Button>
            </>
          ) : null}
          {po.status !== "canceled" && po.status !== "received" ? (
            <Button variant="secondary" onClick={() => setPanel(panel === "email" ? "" : "email")}>
              Email to supplier
            </Button>
          ) : null}
          {openPo ? (
            <>
              <Button onClick={() => setPanel(panel === "receive" ? "" : "receive")}>Receive</Button>
              <AckUpload tenantId={tenantId} poId={po.id} onResult={setAck} say={setMsg} />
            </>
          ) : null}
          <Button variant="ghost" onClick={() => void pdf()}>
            PDF
          </Button>
          {po.status !== "canceled" && po.status !== "received" && !po.lines.some((l) => l.receivedQty > 0) ? (
            <ConfirmButton className="px-2 py-1 text-xs" confirm={`Cancel PO #${po.number}? Parts it was ordering go back to "to order".`} onConfirm={() => act("setPurchaseOrderStatus", { action: "cancel" }, "Canceled.")}>
              Cancel PO
            </ConfirmButton>
          ) : null}
        </div>

        {panel === "email" ? <PoEmail tenantId={tenantId} po={po} onDone={(m) => { setMsg(m); setPanel(""); }} /> : null}
        {panel === "receive" ? <PoReceive po={po} busy={busy} onReceive={(lines) => act("receivePurchaseOrder", { lines }, "Received into stock.")} /> : null}
        {ack ? <AckReview tenantId={tenantId} po={po} ack={ack} onDone={(m) => { setAck(null); setMsg(m); }} /> : null}
      </Card>
    </div>
  );
}

function PoEmail({ tenantId, po, onDone }: { tenantId: string; po: PoRow; onDone: (msg: string) => void }) {
  const [to, setTo] = useState(po.vendorEmail);
  const [cc, setCc] = useState("");
  const [ccMe, setCcMe] = useState(true);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const split = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
  async function send() {
    if (!split(to).length) return setError("Add the supplier's email.");
    setBusy(true);
    setError("");
    try {
      await call("emailPurchaseOrder", { tenantId, poId: po.id, to: split(to), cc: split(cc), ccMe, message }, { timeoutMs: 120_000 });
      onDone(`PO #${po.number} emailed to ${split(to).join(", ")}.`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-2 rounded-lg border border-steel/20 bg-cream/40 p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label>To</Label>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="orders@supplier.com" />
        </div>
        <div>
          <Label>CC</Label>
          <Input value={cc} onChange={(e) => setCc(e.target.value)} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-ink">
        <input type="checkbox" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} /> Copy me
      </label>
      <textarea className={textareaClass} rows={2} value={message} placeholder="Message (optional)" onChange={(e) => setMessage(e.target.value)} />
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button busy={busy} onClick={() => void send()}>
        {po.status === "draft" ? "Place and email" : "Send email"}
      </Button>
    </div>
  );
}

function PoReceive({ po, busy, onReceive }: { po: PoRow; busy: boolean; onReceive: (lines: Array<{ key: string; quantity: number }>) => void }) {
  const [qty, setQty] = useState<Record<string, string>>(() => Object.fromEntries(po.lines.map((l) => [l.key, String(Math.max(0, l.quantity - l.receivedQty))])));
  const lines = po.lines.filter((l) => l.receivedQty < l.quantity);
  return (
    <div className="space-y-1 rounded-lg border border-steel/20 bg-cream/40 p-3">
      <p className="text-xs text-steel">What came in? Pricebook items go into stock; a job's part shows arrived once all of it is in.</p>
      {lines.map((l) => (
        <div key={l.key} className="flex items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 truncate text-ink">{l.description}</span>
          <span className="text-xs text-steel">{l.quantity - l.receivedQty} open</span>
          <Input className="w-20 py-1" type="number" min="0" step="1" value={qty[l.key] ?? ""} onChange={(e) => setQty((q) => ({ ...q, [l.key]: e.target.value }))} aria-label={`Received of ${l.description}`} />
        </div>
      ))}
      <Button busy={busy} onClick={() => onReceive(lines.map((l) => ({ key: l.key, quantity: parseFloat(qty[l.key] ?? "0") || 0 })).filter((l) => l.quantity > 0))}>
        Receive
      </Button>
    </div>
  );
}

// ---- The supplier's confirmation ---------------------------------------------

interface AckResult {
  poId: string | null;
  poNumber: number | null;
  vendorName: string | null;
  quotedPo: string | null;
  vendorRef: string;
  expectedAt: number | null;
  matches: Array<{ key: string; description: string; poQty: number; poCostCents: number | null; ackQty: number | null; ackCostCents: number | null; note: string }>;
  extras: string[];
  missing: string[];
  storagePath: string;
  fileName: string;
}

async function readAck(tenantId: string, file: File, poId?: string): Promise<AckResult> {
  const mediaType = file.type || "application/pdf";
  if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(mediaType)) throw new Error("PDF or photo (JPG/PNG) only.");
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return call<AckResult>("readOrderAcknowledgment", { tenantId, docBase64: btoa(binary), mediaType, fileName: file.name.slice(0, 200), ...(poId ? { poId } : {}) }, { timeoutMs: 300_000 });
}

/** Upload a supplier confirmation: from a PO (read against it), or from the list (found by the PO number it quotes). */
function AckUpload({ tenantId, poId, onResult, onRead, say }: { tenantId: string; poId?: string; onResult?: (a: AckResult) => void; onRead?: (poId: string) => void; say: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className={cn("inline-flex cursor-pointer items-center gap-1 rounded-lg border border-steel/30 bg-white px-3 py-1.5 text-sm text-ink hover:border-steel", busy && "pointer-events-none opacity-60")}>
      <FileUp size={14} /> {busy ? "Reading…" : poId ? "Upload confirmation" : "Read a supplier confirmation"}
      <input
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f) return;
          setBusy(true);
          say("");
          try {
            const r = await readAck(tenantId, f, poId);
            if (!r.poId) say(`Couldn't tell which PO that confirms${r.quotedPo ? ` (it quotes "${r.quotedPo}")` : ""}. Open the PO and upload it there.`);
            else if (onResult) onResult(r);
            else if (onRead) {
              sessionStorage.setItem(`clevis.ack.${r.poId}`, JSON.stringify(r));
              onRead(r.poId);
            }
          } catch (err) {
            say(errText(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </label>
  );
}

function AckReview({ tenantId, po, ack, onDone }: { tenantId: string; po: PoRow; ack: AckResult; onDone: (msg: string) => void }) {
  const differs = (m: AckResult["matches"][number]) => (m.ackCostCents !== null && m.ackCostCents !== m.poCostCents) || (m.ackQty !== null && m.ackQty !== m.poQty) || !!m.note;
  const [take, setTake] = useState<Record<string, { cost: boolean; qty: boolean }>>(() =>
    Object.fromEntries(ack.matches.map((m) => [m.key, { cost: m.ackCostCents !== null && m.ackCostCents !== m.poCostCents, qty: false }])),
  );
  const [expected, setExpected] = useState(toDateInput(ack.expectedAt ?? po.expectedAt));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const differences = [
    ...ack.matches.filter(differs).map((m) => [
      m.description,
      m.ackCostCents !== null && m.ackCostCents !== m.poCostCents ? `${m.poCostCents !== null ? money(m.poCostCents) : "no cost"} → ${money(m.ackCostCents)}` : "",
      m.ackQty !== null && m.ackQty !== m.poQty ? `qty ${m.poQty} → ${m.ackQty}` : "",
      m.note,
    ].filter(Boolean).join(": ")),
    ...ack.missing.map((d) => `${d}: not on their confirmation`),
    ...ack.extras.map((d) => `Also on theirs: ${d}`),
  ].map((d) => d.slice(0, 300));

  async function apply() {
    setBusy(true);
    setError("");
    try {
      await call("applyOrderAcknowledgment", {
        tenantId,
        poId: po.id,
        vendorRef: ack.vendorRef,
        expectedAt: fromDateInput(expected),
        fileName: ack.fileName,
        storagePath: ack.storagePath,
        lines: ack.matches.filter((m) => take[m.key]?.cost || take[m.key]?.qty || m.note).map((m) => ({ key: m.key, unitCostCents: take[m.key]?.cost ? m.ackCostCents : null, quantity: take[m.key]?.qty ? m.ackQty : null, note: m.note })),
        differences,
      });
      onDone(`Confirmation saved on PO #${po.number}.`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-brandorange/40 bg-brandorange/5 p-3">
      <p className="text-sm font-semibold text-ink">
        Supplier confirmation{ack.vendorRef ? ` ${ack.vendorRef}` : ""} for PO #{ack.poNumber}
      </p>
      <div className="divide-y divide-steel/10 text-sm">
        {ack.matches.map((m) => (
          <div key={m.key} className="flex flex-wrap items-center gap-2 py-1">
            <span className="min-w-0 flex-1 truncate text-ink">{m.description}</span>
            {m.ackQty === null ? (
              <span className="text-xs text-danger">not on their confirmation</span>
            ) : (
              <>
                <span className={cn("text-xs", m.ackQty !== m.poQty ? "text-brandorange-dark" : "text-steel")}>
                  qty {m.poQty}
                  {m.ackQty !== m.poQty ? ` → ${m.ackQty}` : " ✓"}
                </span>
                <span className={cn("text-xs", m.ackCostCents !== null && m.ackCostCents !== m.poCostCents ? "text-brandorange-dark" : "text-steel")}>
                  {m.poCostCents !== null ? money(m.poCostCents) : "no cost"}
                  {m.ackCostCents !== null && m.ackCostCents !== m.poCostCents ? ` → ${money(m.ackCostCents)}` : m.ackCostCents !== null ? " ✓" : ""}
                </span>
                {m.ackCostCents !== null && m.ackCostCents !== m.poCostCents ? (
                  <label className="flex items-center gap-1 text-xs text-ink">
                    <input type="checkbox" checked={take[m.key]?.cost ?? false} onChange={(e) => setTake((t) => ({ ...t, [m.key]: { ...(t[m.key] ?? { qty: false }), cost: e.target.checked } }))} /> use their price
                  </label>
                ) : null}
                {m.ackQty !== m.poQty ? (
                  <label className="flex items-center gap-1 text-xs text-ink">
                    <input type="checkbox" checked={take[m.key]?.qty ?? false} onChange={(e) => setTake((t) => ({ ...t, [m.key]: { ...(t[m.key] ?? { cost: false }), qty: e.target.checked } }))} /> change qty
                  </label>
                ) : null}
              </>
            )}
            {m.note ? <span className="w-full text-[11px] text-brandorange-dark">Supplier: {m.note}</span> : null}
          </div>
        ))}
      </div>
      {ack.extras.length ? <p className="text-xs text-steel">Also on their confirmation: {ack.extras.join("; ")}</p> : null}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label>Ships for</Label>
          <Input type="date" className="w-44" value={expected} onChange={(e) => setExpected(e.target.value)} />
        </div>
        <Button busy={busy} onClick={() => void apply()}>
          Save confirmation
        </Button>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
